import React, { useEffect, useState, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useData } from './stores/data';
import { tw } from './index';
import i18n from './i18n';
import { getApiUrl } from './config';
import { getExperienceById } from './services/experienceID';
import type { EbsProps } from './index';
import EcoBeautyScore from './ui/EcoBeautyScore';
import Csr from './ui/Csr';
import { useTrackEvent } from './hooks/useTrackEvent';
import EbsLogo from './components/EbsLogo';
import { getBrandData } from './services/brands';
import useAuth from './hooks/useAuth';

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export const convertCategoryToNewFormat = (category: string) => {
  return category.includes('.') ? category : `${category.slice(0, 1)}.${category.slice(1, 2)}`
}

const isValidCategory = (category: string | undefined, categories: string[]) => {
  if (!category) {
    return false
  }

  const newFormatCategories = categories.map((cat) => convertCategoryToNewFormat(cat));

  return newFormatCategories.includes(convertCategoryToNewFormat(category))
}

const Ebs: React.FC<EbsProps> = ({ experienceId, lang, gtin, moduleMode }) => {
  const { isAuthenticated, login, idToken } = useAuth()
  const { data, setData, setGtin, setExperienceId } = useData();

  const { t } = useTranslation();

  const isLargeScreen = useMediaQuery({ minWidth: 1024 });
  const isMobileScreen = useMediaQuery({ maxWidth: 767 });

  const hasEventBeenTriggered = useRef(false);
  const trackEvent = useTrackEvent();

  const [loaded, setLoaded] = useState(false);
  const [brand, setBrand] = useState('');
  const [country, setCountry] = useState('');
  const [fontTitleColor, setFontTitleColor] = useState('');
  const [fontTitle, setFontTitle] = useState('');
  const [fontBodyColor, setFontBodyColor] = useState('');
  const [primaryStroke, setPrimaryStroke] = useState('');
  const [secondaryStroke, setSecondaryStroke] = useState('');
  const [monochrome, setMonoCrome] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [moduleType, setModuleType] = useState('');


  const validCategory = isValidCategory(data?.EBS?.EBSCategory, categories)
  const isRTL = i18n.dir() === 'rtl';

  const brandData = getBrandData(brand, country)

  useEffect(() => {
    if (!data) return;

    if (!hasEventBeenTriggered.current) {
      trackEvent({
        event: 'uaevent',
        event_name: 'service_start',
      });
      hasEventBeenTriggered.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (!experienceId) throw new Error('experience-id is required');
    if (!gtin) throw new Error('gtin is required');

    lang && i18n.changeLanguage(lang);

    const cfg = getExperienceById(experienceId) || {};

    setGtin(gtin);
    setExperienceId(experienceId);

    setBrand(cfg.brand_name || '');
    setCountry(cfg.brand_country || '');
    setFontTitleColor(cfg.brand_title_color || '');
    setFontBodyColor(cfg.brand_body_color || '');
    setFontTitle(cfg.brand_title_font || '');
    setPrimaryStroke(cfg.brand_primary_stroke || '');
    setSecondaryStroke(cfg.brand_secondary_stroke || '');
    setMonoCrome(cfg.monochrome_color || '');
    setCategories(cfg.brand_categories || []);
    setModuleType(cfg.module_type || '');

    // Wait for authentication state to be determined before proceeding
    if (isAuthenticated === undefined) {
      // Authentication state is still being determined
      return;
    }

    if (!isAuthenticated) {
      login()
    } else if (idToken) {
      (async () => {
        try {
          const paddedGTIN = gtin.padStart(14, '0');
          const res = await fetch(
            `${getApiUrl()}/products/${paddedGTIN}`,
            {
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            }
          );
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          setData(await res.json());
        } catch (err) {
          setData(null);
          console.error('Data for gtin ' + gtin.padStart(14, '0') + ' could not be retrieved');
        } finally {
          setLoaded(true);
        }
      })();
    }


  }, [experienceId, lang, gtin, isAuthenticated, idToken]);

  if (!loaded) {
    return null
  }

  // Resolve module to display
  moduleMode = moduleMode || (moduleType === 'score_only' ? 'ebsMode' : 'fullMode');

  const BrandIsWorkingToSupport = () => {
    if (!data?.EBS || !validCategory) {
      return (
        <div className={tw`max-w-6xl p-0 md:flex md:flex-row`}>
          <div className={tw`flex w-1/3 ${isRTL ? 'pl-6' : 'pr-6'} md:w-1/4 lg:w-1/4`}>
            <EbsLogo />
          </div>
          <div className={tw`w-full`}>
            <div
              className={tw`mx-auto pb-6 ${isRTL ? 'pr-6' : 'pl-6'} pt-0 leading-7 space-y-4 ${fontBodyColor}`}
              role='text'
            >
              {
                t('Brand_declaration_when_EBS_is_not_shown', {
                  brand,
                  launchYear: brandData ? brandData['Launch year'] : '2020',
                }).split('<br><br>').map((paragraph: string, index: number) => (
                  <p key={`paragraph-${index}`}>
                    <Trans
                      components={{
                        semibold: <span className={tw`font-semibold`} />,
                      }}
                    >
                      {paragraph}
                    </Trans>
                  </p>
                ))
              }
            </div>
          </div>
        </div>
      )
    } else {
      return null;
    }
  };

  // If data not available or category not supported => return null or fallback to CSR
  if (
    moduleMode === 'ebsMode' &&
    (!data || !data.EBS?.EBSCategory || !validCategory)
  ) {
    return null;
  }
  if (!data?.EBS || (!validCategory && moduleMode !== 'ebsMode')) {
    moduleMode = 'csrMode';
  }

  // Helpers
  const Score = () => (
    <EcoBeautyScore
      score={data?.EBS?.EBSFootprintScore ?? ''}
      category={convertCategoryToNewFormat(data?.EBS?.EBSCategory ?? '')}
      monochrome={monochrome}
    />
  );
  const LearnMore = () => (
    <div className={tw`text-xs hover:underline inline-block pb-0 ${fontBodyColor}`}>
      <a
        href={brandData?.['Link to EBS landing page'] || 'https://www.ecobeautyscore.com'}
        aria-label={t('Learn_more_about_EcoBeautyScore')}
        target='_blank'
        rel='noopener noreferrer'
        className={tw`underline`}
        onClick={() => {
          trackEvent({
            event: 'uaevent',
            event_name: 'click',
            event_detail: 'learn-ebs',
          });
        }}
      >
        {t('Learn_more_about_EcoBeautyScore')}
      </a >
    </div >
  );

  return (
    <div
      className='Ebs'
    >
      {moduleMode === 'ebsMode' && (
        <div className={tw`min-w-[225px] max-w-[225px]`}>
          <Score />
        </div>
      )}
      {(moduleMode === 'fullMode' || moduleMode === 'csrMode') && (
        <div className={tw`max-w-6xl p-0 flex lg:flex-row font-[Inter]`}>
          {moduleMode === 'fullMode' && isLargeScreen && (
            <div className={tw`w-1/2 pt-16`}>
              <div
                className={tw`container mx-auto flex flex-col items-center min-h-[190px] max-h-[500px]`}
                aria-labelledby='score-heading'
              >
                <Score />
              </div>
            </div>
          )}
          <div className={tw`w-full ${isRTL ? 'pr-6' : 'pl-6'}`}>
            <BrandIsWorkingToSupport />
            {moduleMode === 'fullMode' && (
              <>
                <div
                  className={tw`mx-auto pb-6 lg:pb-0 leading-7 space-y-4 ${fontBodyColor}`}
                  role='text'
                >
                  <p>
                    <Trans
                      i18nKey='Intro-1'
                      values={{ brand }}
                      components={{
                        semibold: <span className={tw`font-semibold`} />,
                      }}
                    />
                  </p>
                </div>
                {!isLargeScreen && (
                  <div
                    className={tw` mx-auto flex flex-col w-full mx-0`}
                    aria-labelledby='score-heading'
                  >
                    <div
                      className={tw`container mx-auto flex flex-col ${isMobileScreen ? 'items-center' : 'items-start'
                        } ${!isMobileScreen && !isLargeScreen ? 'mx-0' : ''}`}
                    >
                      <div className={tw`content-center`}>
                        <Score />
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={tw`mx-auto pb-6 pt-6 lg:pb-3 leading-7 space-y-4 ${fontBodyColor}`}
                  role='text'
                >
                  <p>
                    <Trans
                      i18nKey='Intro-2'
                      values={{ brand, categoryName: t(data?.EBS?.EBSCategory ?? '') }}
                      components={{ semibold: <span className={tw`font-semibold`} /> }}
                    />
                  </p>
                </div>
                <div
                  className={tw`container flex flex-col items-center pb-6 block`}
                  aria-labelledby='score-heading'
                >
                  <LearnMore />
                </div>
                <div className={tw`pb-6 pt-2 pb-3 space-y-6`}>
                  <div className={tw`${primaryStroke} w-full not-sr-only`} role='separator'></div>
                </div>
              </>
            )}
            {(moduleMode === 'csrMode' || moduleMode === 'fullMode') && (
              <Csr
                dt={data}
                fontTitleColor={fontTitleColor}
                fontTitle={fontTitle}
                fontBodyColor={fontBodyColor}
                divider={secondaryStroke}
                module={moduleMode}
                brand={brand}
                country={country}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Ebs;
